import {FilterTypeComponentMap, FilterTypes} from "./FilterTypes";

// Class for the filter manager
import {Filter} from "./Filter";
import {Component, html} from "htm/preact";
import register from "preact-custom-element";
import {TextSearch} from "./filters/TextSearch";

interface State {
    filters: Filter<any, any>[];
    activeFilters: Filter<any, any>[];
    changed: boolean;
}

interface FiltersManagerProps {
    filterdata?: string;
}

export class FiltersManager extends Component<FiltersManagerProps, State> {

    readonly filters: Filter<any, any>[] = [];
    searchFilter: any;
    activeFilters: Filter<any, any>[] = [];
    initialQueryParams: Record<string, string> | undefined;
    initialLoad: boolean = true;
    lastQueryParams: Record<string, string> | undefined;
    activeFiltersCount: number;

    filtersJson: any = {};

    constructor(props: FiltersManagerProps) {
        super(props);

        this.activeFiltersCount = 0;

        this.handleClearFilters = this.handleClearFilters.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleReset = this.handleReset.bind(this);

        const filtersJsonString = this.props["filterdata"];

        if(filtersJsonString){
            this.filtersJson = JSON.parse(filtersJsonString);
        }

        this.searchFilter = this.filtersJson.find((f: any) => f.type == FilterTypes.Freetext_Search);
        this.state = {filters: this.filters, activeFilters: this.activeFilters, changed: false};

    }

    private createComponent(filter: any) {
        const type: FilterTypes = filter.type;
        const comp = FilterTypeComponentMap[type];
        if (comp && type != FilterTypes.Freetext_Search) {
            return { type: comp, props: filter };
        }
        return null;
    }

    addActiveFilter(filter: Filter<any, any>) {
        this.setState(currentState => currentState.activeFilters.some(candidate => candidate.id === filter.id)
            ? currentState
            : {...currentState, activeFilters: [...currentState.activeFilters, filter]});
    }

    removeActiveFilter(filter: Filter<any, any>){
        this.setState(currentState => ({
            ...currentState,
            activeFilters: currentState.activeFilters.filter(candidate => candidate.id !== filter.id)
        }));
    }

    addFilter(filter: Filter<any, any>) {
        this.filters.push(filter);
    }

    notifyFilterChanged(filter: Filter<any, any>) {
        this.filters
            .filter((candidate: any) => candidate.props.dependsOn === filter.id && typeof candidate.loadDependentOptions === "function")
            .forEach((candidate: any) => candidate.loadDependentOptions(filter.state.value?.selected || []));
    }

    private renderFilter(filter: any, index: number) {
        const comp = this.createComponent(filter);
        if (!comp) {
            return;
        }

        const control = html`
            <div class="delta-filter-container">
                ${filter.hint ? html`<div class="govuk-hint govuk-!-margin-bottom-1">${filter.hint}</div>` : null}
                <${comp.type} key=${comp.props.id} ...${comp.props} filtersManager=${this} />
            </div>
        `;

        if (filter.subgroup) {
            return html`
                <details class="govuk-details" open=${index === 0}>
                    <summary class="govuk-details__summary">
                        <span class="govuk-details__summary-text">${filter.subgroup}</span>
                    </summary>
                    <div class="govuk-details__text">${control}</div>
                </details>
            `;
        }

        return control;
    }

    private sectionId(section: string) {
        return section.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    }

    private sectionIsActive(section: string) {
        return this.state.activeFilters.some(filter => ((filter.props as any).section || filter.name) === section);
    }

    private updateSectionControls(filter: Filter<any, any>) {
        const section = (filter.props as any).section || filter.name;

        const active = this.state.activeFilters.some(candidate => ((candidate.props as any).section || candidate.name) === section);
        document.getElementById(`delta-filters-active-section_${this.sectionId(section)}`)?.classList.toggle("hidden", !active);
        document.getElementById(`delta-filters-clear-section_${this.sectionId(section)}`)?.classList.toggle("hidden", !active);
    }

    private clearSection(filters: any[]) {
        const sectionFilters = this.filters.filter(filter => filters.some(candidate => candidate.id === filter.id));
        Promise.all(sectionFilters.map(filter => new Promise<void>(resolve => filter.clear(resolve, true))))
            .then(() => this.handleApply());
    }

    private renderSectionFilterControls(filters: any[]) {
        return html`${filters.map((filter: any, index: number) => this.renderFilter(filter, index))}`;
    }

    render() {
        const searchFilterHTML = html`
            <${TextSearch} filtersManager=${this} preloadedState="${this.searchFilter.preloadedState}" key="freetext" defaultSearch="true" id="freetext" name="Search" type="text"/>`;

        const sectionOrder: string[] = [];
        const sections = new Map<string, any[]>();
        this.filtersJson
            .filter((filter: any) => filter.type != FilterTypes.Freetext_Search)
            .forEach((filter: any) => {
                const section = filter.section || filter.name;
                if (!sections.has(section)) {
                    sectionOrder.push(section);
                    sections.set(section, []);
                }
                sections.get(section)?.push(filter);
            });

        const filtersHTML = sectionOrder.map((section: string) => {
            const sectionFilters = sections.get(section) || [];
            const sectionId = section.toLowerCase().replace(/[^a-z0-9]+/g, "-");

            return html`
                <div class="delta-filter-container">
                    <div class="delta-filters-accordion-section govuk-accordion__section">
                        <div class="delta-filters-accordion-section-header govuk-accordion__section-header">
                            <h2 class="delta-filters-accordion-section-heading govuk-accordion__section-heading">
                                <span class="delta-filters-accordion-section-buttons govuk-accordion__section-button delta-filter-title" aria-controls="filter-controls-${sectionId}">
                                    ${section}
                                    <span class="delta-filters-active-tag govuk-tag ${this.sectionIsActive(section) ? "" : "hidden"}" id="delta-filters-active-section_${this.sectionId(section)}">Active</span>
                                    <span class="delta-filters-clear-filter-link ${this.sectionIsActive(section) ? "" : "hidden"}" id="delta-filters-clear-section_${this.sectionId(section)}" onClick=${() => this.clearSection(sectionFilters)}>Clear</span>
                                </span></h2>
                        </div>
                        <div id="filter-controls-${sectionId}" class="delta-filters-accordion-section-contents govuk-accordion__section-content">
                            ${this.renderSectionFilterControls(sectionFilters)}
                        </div>
                    </div>
                </div>
            `;
        });

        return html`
            <div class="js-sliding-panel-content delta-table-filters" aria-hidden="true" id="filters-sliding-content">
                <div class="delta-filters_header">
                    <span class="govuk-heading-m govuk-!-margin-bottom-0 delta-filters-header-components">Filters</span>
                     
                    <span class="active-filters-count govuk-tag delta-filters-header-components ${this.state.activeFilters.length > 0 ? "" : "active-filters-count-empty"}">
                        ${this.state.activeFilters.length > 0 ? this.state.activeFilters.length + " active" : null}
                    </span>
                    <span class="active-filters-summary-smaller govuk-tag delta-filters-header-components"></span>
                </div>
                <div class="delta-filters_container">
                    ${searchFilterHTML} 

                    <div class="js-search-form govuk-accordion" data-module="govuk-accordion" id="filter-accordion">
                        ${filtersHTML}
                    </div>
                </div>
                <div class="delta-filters_controls">
                    <button class="govuk-button" id="filters-apply-button" disabled type="submit" onClick=${this.handleApply}>Apply filters</button>
                    <button type="button" class="govuk-button" id="filters-clear-button" onClick=${this.handleClearFilters}>Clear filters</button>
                    <button type="button" class="govuk-button filters-reset-button hidden" id="filters-reset-button" onClick=${this.handleReset}>Reset filters</button>
                </div>
            </div>
        `;
    }

    componentDidMount() {
        (window as any).filtersManager = this;
        this.initialQueryParams = this.getQueryParams();
    }

    public loadedInitialParams(){
        this.initialLoad = false;
    }

    private handleClearFilters(){
        let promises: Promise<void>[] = [];
        this.filters.forEach(f => promises.push(new Promise<void>((resolve) => {
            f.clear(() => {
                resolve();
            }, true)
        })));

        Promise.all(promises).then(() => {
            this.handleApply();
        });
    };

    private getQueryParams() {
        let queryParams = {} as any;


        for(let i = 0; i < this.filters.length; i++){
            //add query params
            let filter = this.filters[i];

            if(!filter.valid){
                filter.setActive(false);
                continue;
            }


            let queryParam = filter.applyFilter();

            //handle setting active state
            if(jQuery.isEmptyObject(queryParam)){
                //set to not active
                filter.setActive(false);
            }
            else{
                //set to active
                filter.setActive(true);
            }

            queryParams = {...queryParams, ...queryParam};
        }
        this.lastQueryParams = queryParams;
        return queryParams;
    }

    public handleTextSearch(val: string){
        let queryParams: Record<string, string>;
        if(this.lastQueryParams != undefined){
            queryParams = this.lastQueryParams;
        }
        else{
            queryParams = {};
        }

        if(val == "" ){
            delete queryParams["freetext"];
            this.handleApply(queryParams, true)
        }
        else{
            queryParams["freetext"] = val;
            this.handleApply(queryParams, true)
        }
    }

    public handleApply(queryParams?: Record<string, string>, textSearch?: boolean) {
        if(!textSearch){
            queryParams = this.getQueryParams();
        }

        // Clear DataGrid params before loading new data
        if ((window as any).DCLG && (window as any).DCLG.DataGrid && (window as any).DCLG.DataGrid.instance) {
            (window as any).DCLG.DataGrid.instance.dclg.params = {};
        }

        (window as any).DCLG.DataGrid.instance.loadData(queryParams);

        this.toggleResetButton(true);
        this.toggleApplyButton(false);

    };

    public handleReset(){
        const promises = this.filters.map(f => new Promise<void>((resolve) => {
            f.reset(() => resolve());
        }));

        Promise.all(promises).then(() => {
            this.handleApply();
            this.toggleResetButton(false);
        });
    }

    toggleApplyError(error: boolean){
        const applyButton = document.getElementById("filters-apply-button");

        if(applyButton){
            if(error){
                applyButton.setAttribute("error", "true");
            }
            else{
                applyButton.removeAttribute("error");
            }
        }
    }

    toggleApplyButton(enabled: boolean){
        const applyButton = document.getElementById("filters-apply-button");
        enabled = enabled || this.filters.some(filter => JSON.stringify(filter.lastAppliedValue) !== JSON.stringify(filter.state.value));

        if(applyButton){
            if(enabled && !applyButton.hasAttribute("error")){ //attr error stops the apply button from being enabled when a filter is in an error state
                applyButton.removeAttribute("disabled");
            }
            else{
                applyButton.setAttribute("disabled", "true");
            }
        }
    }

    toggleResetButton(enabled: boolean){
        const resetButton = document.getElementById("filters-reset-button");

        if(resetButton){
            if(enabled){
                resetButton.classList.remove("hidden");
            }
            else{
                resetButton.classList.add("hidden");
            }
        }
    }

}

register(FiltersManager, "table-filters");
